import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import './AccountSettings.css';

function DeleteAccountForm({ onClose }) {
  const { t } = useTranslation(['profile', 'shared']);
  const [reason, setReason] = useState('');
  const [customFeedback, setCustomFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const reasons = [
    'not_using',
    'privacy_concerns',
    'too_many_notifications',
    'technical_issues',
    'other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!reason) {
      setError(t('profile.settings.pages.account.deleteForm.errorNoReason'));
      return;
    }

    if (reason === 'other' && !customFeedback.trim()) {
      setError(t('profile.settings.pages.account.deleteForm.errorNoCustomFeedback'));
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error(t('profile.settings.pages.account.deleteForm.errorNotLoggedIn'));
      }

      // 1. Zapisanie ankiety do account_actions
      const { error: insertError } = await supabase
        .from('account_actions')
        .insert({
          user_id: user.id,
          action_type: 'delete',
          reason: reason,
          feedback: reason === 'other' ? customFeedback.trim() : null
        });

      if (insertError) {
        throw insertError;
      }

      // 2. Usunięcie użytkownika
      // Uwaga: Usunięcie z auth.users wymaga uprawnień admin lub Edge Function
      // Na razie usuniemy wszystkie dane z public.* i oznaczymy jako usunięte
      // W przyszłości można użyć Edge Function do pełnego usunięcia
      
      // Usuń wszystkie dane użytkownika z public.* (CASCADE powinno to zrobić automatycznie)
      // Ale najpierw usuńmy ręcznie dane z tabel które mogą mieć relacje
      
      // Usunięcie z auth.users wymaga Edge Function lub admin API
      // Na razie wylogujmy użytkownika i przekierujmy
      // W produkcji powinno być Edge Function które usunie użytkownika z auth.users
      
      await supabase.auth.signOut();
      
      // Przekierowanie do strony logowania
      window.location.href = '/';
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message || t('profile.settings.pages.account.deleteForm.errorDelete'));
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  if (showConfirm) {
    return (
      <div className="account-form-container">
        <h3 className="account-form-title account-form-title-danger">
          {t('profile.settings.pages.account.deleteForm.confirmTitle')}
        </h3>

        <div className="account-form-confirm-message account-form-confirm-message-danger">
          <p>{t('profile.settings.pages.account.deleteForm.confirmMessage')}</p>
          <p className="account-form-warning-text">
            {t('profile.settings.pages.account.deleteForm.warning')}
          </p>
        </div>

        {error && (
          <div className="account-form-error">
            {error}
          </div>
        )}

        <div className="account-form-actions">
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            className="account-form-button account-form-button-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="account-form-button account-form-button-danger"
            disabled={loading}
          >
            {loading
              ? t('profile.settings.pages.account.deleteForm.deleting')
              : t('profile.settings.pages.account.deleteForm.confirmButton')
            }
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-form-container">
      <h3 className="account-form-title">
        {t('profile.settings.pages.account.deleteForm.title')}
      </h3>

      <form onSubmit={handleSubmit} className="account-form">
        <div className="account-form-field">
          <label className="account-form-label">
            {t('profile.settings.pages.account.deleteForm.reasonLabel')}
          </label>
          <div className="account-form-radio-group">
            {reasons.map((reasonKey) => (
              <label key={reasonKey} className="account-form-radio">
                <input
                  type="radio"
                  name="reason"
                  value={reasonKey}
                  checked={reason === reasonKey}
                  onChange={(e) => setReason(e.target.value)}
                />
                <span>{t(`profile.settings.pages.account.deleteForm.reasons.${reasonKey}`)}</span>
              </label>
            ))}
          </div>
        </div>

        {reason === 'other' && (
          <div className="account-form-field">
            <label htmlFor="customFeedback" className="account-form-label">
              {t('profile.settings.pages.account.deleteForm.customFeedbackLabel')}
            </label>
            <textarea
              id="customFeedback"
              value={customFeedback}
              onChange={(e) => setCustomFeedback(e.target.value)}
              placeholder={t('profile.settings.pages.account.deleteForm.customFeedbackPlaceholder')}
              className="account-form-textarea"
              rows={4}
            />
          </div>
        )}

        {error && (
          <div className="account-form-error">
            {error}
          </div>
        )}

        <div className="account-form-info account-form-info-warning">
          {t('profile.settings.pages.account.deleteForm.info')}
        </div>

        <div className="account-form-actions">
          <button
            type="button"
            onClick={onClose}
            className="account-form-button account-form-button-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="account-form-button account-form-button-danger"
            disabled={loading || !reason}
          >
            {t('profile.settings.pages.account.deleteForm.submitButton')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DeleteAccountForm;





